/** Local hour (0–23) when light mode begins. Inclusive. */
export const DAY_START_HOUR = 6;

/** Local hour (0–23) when dark mode begins. Exclusive end of light mode. */
export const DAY_END_HOUR = 18;

export const THEME_STORAGE_KEY = "cloudaxe-theme";

export type Theme = "light" | "dark";
export type ThemePreference = Theme | "auto";

/** Light during local daylight hours; dark otherwise. */
export function getThemeFromLocalTime(date: Date = new Date()): Theme {
  const hour = date.getHours();
  return hour >= DAY_START_HOUR && hour < DAY_END_HOUR ? "light" : "dark";
}

/** Read stored preference. Missing or invalid values mean automatic (time-based). */
export function getStoredThemePreference(): ThemePreference {
  if (typeof window === "undefined") return "auto";
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    // ignore
  }
  return "auto";
}

export function getEffectiveTheme(
  preference: ThemePreference,
  date: Date = new Date()
): Theme {
  if (preference === "light" || preference === "dark") return preference;
  return getThemeFromLocalTime(date);
}

export function applyThemeClass(theme: Theme): void {
  const root = document.documentElement;
  if (theme === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
}

export function persistThemePreference(preference: ThemePreference): void {
  try {
    if (preference === "auto") {
      window.localStorage.removeItem(THEME_STORAGE_KEY);
    } else {
      window.localStorage.setItem(THEME_STORAGE_KEY, preference);
    }
  } catch {
    // ignore
  }
}

/** Milliseconds until the next automatic light/dark boundary (local time). */
export function msUntilNextAutoThemeChange(date: Date = new Date()): number {
  const hour = date.getHours();
  const next = new Date(date);

  if (hour >= DAY_START_HOUR && hour < DAY_END_HOUR) {
    next.setHours(DAY_END_HOUR, 0, 0, 0);
  } else if (hour >= DAY_END_HOUR) {
    next.setDate(next.getDate() + 1);
    next.setHours(DAY_START_HOUR, 0, 0, 0);
  } else {
    next.setHours(DAY_START_HOUR, 0, 0, 0);
  }

  return Math.max(next.getTime() - date.getTime(), 1_000);
}
